import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

/* ==========================================================================
   MEDIA: one way to put a product screen on a marketing page, whether it is
   a still or a demo video, in whichever frame the surface lives in (a TV, a
   browser, a phone). Pages describe media as data; this file decides how it
   renders, so the two persona pages stop carrying copy-pasted figure markup.

   Stills and videos come out of the same capture runs
   (scripts/capture-marketing.ts), so a video's poster is a real frame of it.
   ========================================================================== */

type Base = {
  /** Describes what the screen shows, for someone who can't see it. */
  alt: string;
  /** Intrinsic size of the file, so the page reserves the space before it loads. */
  width: number;
  height: number;
};

export type Media =
  | (Base & { kind: 'image'; src: string })
  | (Base & {
      kind: 'video';
      /** webm or mp4, silent, captions burned in. */
      src: string;
      /** A frame of the video, shown before it plays and instead of it under
          reduced motion. */
      poster: string;
    });

/** Where the screen lives. A phone shot stays phone-sized instead of filling
    the column; a TV gets a bezel; a browser gets three dots. */
export type Frame = 'plain' | 'browser' | 'phone' | 'tv';

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * A silent demo that plays only while it is on screen. Muted + playsInline is
 * what lets mobile Safari autoplay it; pausing off screen keeps a page with
 * several of these from decoding all of them at once. Under reduced motion it
 * never starts by itself and shows controls instead, so the person decides.
 */
const DemoVideo: React.FC<{ media: Extract<Media, { kind: 'video' }>; className?: string }> = ({
  media,
  className,
}) => {
  const ref = useRef<HTMLVideoElement>(null);
  const [reduced] = useState(prefersReducedMotion);

  useEffect(() => {
    const video = ref.current;
    if (!video || reduced || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // play() rejects when the browser refuses autoplay; the poster stays,
          // which is the right fallback, so there is nothing to handle.
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [reduced]);

  return (
    <video
      ref={ref}
      className={cn('block w-full h-auto', className)}
      src={media.src}
      poster={media.poster}
      width={media.width}
      height={media.height}
      muted
      loop
      playsInline
      preload="none"
      controls={reduced}
      aria-label={media.alt}
    />
  );
};

const MediaBody: React.FC<{ media: Media; eager?: boolean }> = ({ media, eager }) =>
  media.kind === 'video' ? (
    <DemoVideo media={media} />
  ) : (
    <img
      src={media.src}
      alt={media.alt}
      width={media.width}
      height={media.height}
      loading={eager ? 'eager' : 'lazy'}
      className="block w-full h-auto"
    />
  );

export const MediaFrame: React.FC<{
  media: Media;
  frame?: Frame;
  caption?: string;
  /** Load immediately (above the fold). */
  eager?: boolean;
  className?: string;
}> = ({ media, frame = 'plain', caption, eager, className }) => {
  const isPhone = frame === 'phone';
  return (
    <figure className={cn(isPhone && 'mx-auto w-full max-w-[280px]', className)}>
      {frame === 'tv' ? (
        // A thin dark bezel reads as a screen on a wall without drawing a
        // whole television around it.
        <div className="rounded-md bg-ink p-1.5 shadow-xl sm:p-2">
          <div className="overflow-hidden rounded-[3px]">
            <MediaBody media={media} eager={eager} />
          </div>
        </div>
      ) : frame === 'phone' ? (
        <div className="overflow-hidden rounded-[1.6rem] border-[6px] border-ink bg-ink shadow-xl">
          <MediaBody media={media} eager={eager} />
        </div>
      ) : frame === 'browser' ? (
        <div className="overflow-hidden rounded-md border border-grey-300 bg-grey-50 shadow-lg">
          <div aria-hidden="true" className="flex items-center gap-1.5 border-b border-grey-200 px-3 py-2">
            <span className="h-2 w-2 rounded-full bg-grey-300" />
            <span className="h-2 w-2 rounded-full bg-grey-300" />
            <span className="h-2 w-2 rounded-full bg-grey-300" />
          </div>
          <MediaBody media={media} eager={eager} />
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-grey-200 bg-grey-50">
          <MediaBody media={media} eager={eager} />
        </div>
      )}
      {caption && (
        <figcaption className={cn('mt-3 text-xs text-grey-600 font-mono', isPhone && 'text-center')}>
          {caption}
        </figcaption>
      )}
    </figure>
  );
};
